<script setup lang="ts">
/**
 * The enterprise's signature template (#1118), on the enterprise's settings for an owner: one signature, written in the
 * same editor as a mail, that signs every mailbox in the enterprise with no signature of its own, people's and
 * members' alike, filled in for each with the placeholders below. Saved or removed here; the API refuses anybody but a
 * signed-in owner, and a placeholder it cannot fill in.
 */
import { Button, ZhSectionHeading } from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { api, type MailSignatureTemplate } from '@/api'
import MailEditor from '@/components/MailEditor.vue'
import { draftIsBlank, draftOfText, emptyDraft, type MailDraft } from '@/lib/mailDraft'
import { hasWords } from '@/lib/mailSignature'

const draft = ref<MailDraft>(emptyDraft())
const saved = ref<MailSignatureTemplate | null>(null)
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)

/** `lead`, and the server's sentence after it when the refusal carried one: a bare code adds nothing. */
function failureText(caught: unknown, lead: string): string {
    const said = caught instanceof Error ? caught.message.trim() : ''
    return /\s/.test(said) ? `${lead} ${said}` : lead
}

function show(template: MailSignatureTemplate): void {
    saved.value = template
    draft.value = template.html === '' ? draftOfText(template.text) : { html: template.html, text: template.text }
}

async function load(): Promise<void> {
    loading.value = true
    try {
        show(await api.mailSignatureTemplate())
    } catch {
        error.value = 'Could not load the signature template.'
    } finally {
        loading.value = false
    }
}

async function save(): Promise<void> {
    if (busy.value || draftIsBlank(draft.value)) return
    busy.value = true
    error.value = null
    notice.value = null
    try {
        show(await api.saveMailSignatureTemplate({ html: draft.value.html, text: draft.value.text }))
        notice.value = 'Signature template saved. The next mail from every mailbox that uses it carries it.'
    } catch (caught) {
        error.value = failureText(caught, 'Could not save the signature template.')
    } finally {
        busy.value = false
    }
}

async function remove(): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    notice.value = null
    try {
        show(await api.clearMailSignatureTemplate())
        notice.value = 'Signature template removed.'
    } catch (caught) {
        error.value = failureText(caught, 'Could not remove the signature template.')
    } finally {
        busy.value = false
    }
}

onMounted(() => void load())
</script>

<template>
	<form class="space-y-2" data-testid="enterprise-signature-template" @submit.prevent="save">
		<ZhSectionHeading>Mail signature</ZhSectionHeading>
		<p class="text-muted-foreground text-sm">
			One signature for every mailbox in the enterprise, people's and AI members' alike. It signs the mail of each
			mailbox that has no signature of its own, filled in for that mailbox. A person can switch it off for their own
			mailbox.
		</p>
		<p class="text-muted-foreground text-xs" data-testid="enterprise-signature-template-placeholders">
			Placeholders: {name} the mailbox's name, {role} a member's roles or a person's job title, {address} the address
			the mail is sent from, {enterprise} the enterprise's name.
		</p>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading the signature template…</p>
		<template v-else>
			<MailEditor
				v-model="draft"
				label="Signature template"
				placeholder="{name}, {role} at {enterprise}"
				:disabled="busy"
				testid="enterprise-signature-template-editor"
			/>
			<div class="flex flex-wrap gap-2">
				<Button type="submit" size="sm" data-testid="enterprise-signature-template-save" :disabled="busy || draftIsBlank(draft)">
					Save template
				</Button>
				<Button
					v-if="hasWords(saved)"
					type="button"
					size="sm"
					variant="outline"
					data-testid="enterprise-signature-template-remove"
					:disabled="busy"
					@click="remove"
				>
					Remove template
				</Button>
			</div>
		</template>
		<p v-if="notice" class="text-sm" data-testid="enterprise-signature-template-notice" aria-live="polite">{{ notice }}</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="enterprise-signature-template-error">{{ error }}</p>
	</form>
</template>
